/**
 * Centralized Payment Status Logic
 * Ensures consistent payment status calculation across all modules
 */

/**
 * Calculate payment status based on amounts
 * @param {number} paidAmount - Amount already paid
 * @param {number} totalAmount - Total amount due
 * @returns {string} - 'pending', 'partial', or 'paid'
 */
export const calculatePaymentStatus = (paidAmount, totalAmount) => {
  const paid = parseFloat(paidAmount) || 0;
  const total = parseFloat(totalAmount) || 0;

  // Handle edge cases
  if (total <= 0) return 'paid'; // No amount due
  if (paid <= 0) return 'pending';   // Nothing paid

  // Use small tolerance for floating point comparison
  const tolerance = 0.1;

  if (paid >= (total - tolerance)) {
    return 'paid'; // Fully paid (with tolerance)
  } else if (paid > 0) {
    return 'partial';   // Partially paid
  } else {
    return 'pending';   // No payment
  }
};

/**
 * Get payment status display text
 * @param {string} status - Payment status ('pending', 'partial', 'paid')
 * @returns {string} - Display text
 */
export const getPaymentStatusText = (status) => {
  switch (status) {
    case 'paid':
      return 'Fully Paid';
    case 'partial':
      return 'Partially Paid';
    case 'pending':
    default:
      return 'Unpaid';
  }
};

/**
 * Get payment status badge classes
 * @param {string} status - Payment status
 * @returns {string} - CSS classes for badge
 */
export const getPaymentStatusBadgeClasses = (status) => {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'partial':
      return 'bg-yellow-100 text-yellow-800';
    case 'pending':
    default:
      return 'bg-red-100 text-red-800';
  }
};

/**
 * Create payment status badge component
 * @param {number} paidAmount - Amount paid
 * @param {number} totalAmount - Total amount
 * @returns {JSX.Element} - Payment status badge
 */
export const PaymentStatusBadge = ({ paidAmount, totalAmount }) => {
  const status = calculatePaymentStatus(paidAmount, totalAmount);
  const text = getPaymentStatusText(status);
  const classes = getPaymentStatusBadgeClasses(status);

  return (
    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${classes}`}>
      {text}
    </span>
  );
};

/**
 * Normalize payment data from different sources
 * @param {Object} data - Payment data object
 * @returns {Object} - Normalized payment data
 */
export const normalizePaymentData = (data) => {
  return {
    id: data.id || data._id,
    totalAmount: parseFloat(data.totalAmount || data.total_amount) || 0,
    paidAmount: parseFloat(data.amountReceived || data.amount_received || data.paid_amount) || 0,
    paymentStatus: data.paymentStatus || data.payment_status || calculatePaymentStatus(
      data.amountReceived || data.amount_received || data.paid_amount || 0,
      data.totalAmount || data.total_amount || 0
    ),
    paymentType: data.paymentType || data.payment_type || 'cash',
    customerName: data.customer?.name || data.customer_name || 'Unknown Customer',
    documentNumber: data.invoiceNumber || data.invoice_number || data.orderNumber || data.order_number || data.documentNumber,
    documentType: data.documentType || (data.invoice_number ? 'invoice' : 'closed_order')
  };
};

/**
 * Validate payment amount
 * @param {number} paymentAmount - Amount to pay
 * @param {number} remainingAmount - Remaining amount due
 * @returns {Object} - Validation result
 */
export const validatePaymentAmount = (paymentAmount, remainingAmount) => {
  // Ensure we're working with numbers, not strings with formatting
  const amount = typeof paymentAmount === 'string' ?
    parseFloat(paymentAmount.replace(/,/g, '')) : parseFloat(paymentAmount) || 0;
  const remaining = typeof remainingAmount === 'string' ?
    parseFloat(remainingAmount.replace(/,/g, '')) : parseFloat(remainingAmount) || 0;

  console.log('🔍 validatePaymentAmount Debug:', {
    paymentAmount,
    remainingAmount,
    amount,
    remaining,
    amountType: typeof paymentAmount,
    remainingType: typeof remainingAmount,
    comparison: `${amount} > ${remaining} = ${amount > remaining}`,
    rawComparison: `Raw: ${paymentAmount} > ${remainingAmount}`,
    parsedComparison: `Parsed: ${amount} > ${remaining}`
  });

  // Additional alert for debugging (temporarily disabled)
  // if (amount > remaining) {
  //   alert(`VALIDATION DEBUG: ${amount} > ${remaining} = TRUE. PaymentAmount: "${paymentAmount}", RemainingAmount: "${remainingAmount}"`);
  // }

  if (amount <= 0) {
    return {
      isValid: false,
      error: 'Payment amount must be greater than 0'
    };
  }

  // Fix floating point precision issues by comparing against rounded value
  // Validates that amount doesn't exceed the displayed remaining balance (rounded to 2 decimals)
  if (amount > Number(remaining.toFixed(1))) {
    return {
      isValid: false,
      error: `Payment amount cannot exceed remaining balance of ₹${remaining.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
    };
  }

  return {
    isValid: true,
    error: null
  };
};
